import { computed, inject, ref, watch } from 'vue'
import { matchSorter } from 'match-sorter'
import _chunk from 'lodash/chunk.js'

const filterOutZeroCount = (filter) =>
  Object.keys(filter).includes('count') ? filter.count > 0 : true

export const useFiltersInjector = ({ stateKey }) => {
  const searchInput = ref('')
  const filtersState = inject('filtersState')
  const availableFilters = inject('availableFilters')
  const tracking = inject('tracking')

  const availableFiltersForStateKey = computed(() => {
    return availableFilters.value[stateKey]
  })
  const trackingForStateKey = computed(() => {
    return tracking[stateKey]
  })
  const filtersStateValue = computed({
    get: () => filtersState.value[stateKey].value,
    set(newValue) {
      filtersState.value[stateKey].value = newValue
    },
  })

  const resultsFromGroups = computed(() => {
    return availableFiltersForStateKey.value.reduce(
      (filters, currentFilter) => {
        const results = matchSorter(currentFilter.elements, searchInput.value, {
          keys: ['label'],
          baseSort: (a, b) => b.item.count - a.item.count,
        }).filter(filterOutZeroCount)
        if (results.length) {
          return [
            ...filters,
            {
              name: currentFilter.name,
              elements: results,
              selectedInGroupCount: filtersStateValue.value.reduce(
                (accumulator, selectedValue) => {
                  if (
                    results.some((result) => result.value === selectedValue)
                  ) {
                    return accumulator + 1
                  } else {
                    return accumulator
                  }
                },
                0,
              ),
            },
          ]
        }
        return filters
      },
      [],
    )
  })

  const results = computed(() => {
    return matchSorter(availableFiltersForStateKey.value, searchInput.value, {
      keys: ['label'],
      baseSort: (a, b) => b.item.count - a.item.count,
      threshold: matchSorter.rankings.STARTS_WITH,
    }).filter(filterOutZeroCount)
  })
  const infiniteScrollId = ref(+new Date())
  const PER_PAGE = 50
  const page = ref(1)
  const totalPages = computed(() => {
    return Math.ceil(results.value.length / PER_PAGE)
  })
  const visibleResults = computed(() => {
    if (isSearching.value) return results.value
    const chunks = _chunk(results.value, PER_PAGE)
    return Array.from({ length: page.value }, (_, i) => chunks[i]).flat()
  })
  const loadMore = ($state) => {
    const nextPage = page.value + 1
    if (nextPage >= totalPages.value) return $state.complete()
    page.value = nextPage
    return $state.loaded()
  }
  const resetPageData = () => {
    page.value = 1
    infiniteScrollId.value = +new Date()
  }
  watch(searchInput, resetPageData)

  const hasSelectedValues = computed(() => {
    if (Array.isArray(filtersStateValue.value))
      return filtersStateValue.value.length
    return filtersStateValue.value
  })

  const clearAll = () => {
    filtersStateValue.value = Array.isArray(filtersStateValue.value) ? [] : null
  }

  const sendSegmentAnalytics = (value) => {
    if (!tracking) return
    if (!Object.keys(tracking).includes(stateKey)) return

    window.segmentAnalytics.track(trackingForStateKey.value.name, {
      user_type: window.segmentAnalytics.userType(),
      [trackingForStateKey.value.valueKey]: value,
    })
  }

  const isSearching = computed(() => searchInput.value.length > 0)

  return {
    filtersStateValue,
    searchInput,
    resultsFromGroups,
    results,
    hasSelectedValues,
    clearAll,
    sendSegmentAnalytics,
    isSearching,
    visibleResults,
    loadMore,
    resetPageData,
    page,
  }
}
